<script setup lang="ts">
import { computed } from 'vue'
import { useOpsStore } from '@/stores/ops'
import { useMedicalPlanStore } from '@/stores/medicalPlan'
import { useIAPStore } from '@/stores/iap'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import type { MedicalPlanData, ICS206AidStation, ICS206Transport, ICS206Hospital } from '@/types'

const props = defineProps<{ incidentId: string }>()
defineEmits<{ prev: []; next: [] }>()

const opsStore = useOpsStore()
const medicalPlanStore = useMedicalPlanStore()
const iapStore = useIAPStore()

const opId = computed(() => opsStore.selectedOpId ?? '')

const plan = computed((): MedicalPlanData => medicalPlanStore.medicalPlan ?? {
  aid_stations: [],
  transportation: [],
  hospitals: [],
  emergency_procedures: '',
  k9_vet_info: '',
  medical_unit_leader: '',
})

async function savePlan(updates: Partial<MedicalPlanData>) {
  if (!opId.value) return
  await medicalPlanStore.saveMedicalPlan(props.incidentId, opId.value, { ...plan.value, ...updates })
}

// Aid stations
async function addAidStation() {
  await savePlan({
    aid_stations: [...plan.value.aid_stations, { name: '', location: '', phone: '', paramedics_on_site: false, level: 'BLS' }],
  })
}

async function updateAidStation(index: number, updates: Partial<ICS206AidStation>) {
  const updated = plan.value.aid_stations.map((s, i) => i === index ? { ...s, ...updates } : s)
  await savePlan({ aid_stations: updated })
}

async function removeAidStation(index: number) {
  await savePlan({ aid_stations: plan.value.aid_stations.filter((_, i) => i !== index) })
}

// Transport
async function addTransport() {
  await savePlan({
    transportation: [...plan.value.transportation, { service: '', location: '', phone: '', level: 'BLS' }],
  })
}

async function updateTransport(index: number, updates: Partial<ICS206Transport>) {
  const updated = plan.value.transportation.map((t, i) => i === index ? { ...t, ...updates } : t)
  await savePlan({ transportation: updated })
}

async function removeTransport(index: number) {
  await savePlan({ transportation: plan.value.transportation.filter((_, i) => i !== index) })
}

// Hospitals
async function addHospital() {
  await savePlan({
    hospitals: [...plan.value.hospitals, { name: '', address: '', phone: '', travel_time_air: '', travel_time_ground: '', trauma_center: false, burn_center: false, helipad: false }],
  })
}

async function updateHospital(index: number, updates: Partial<ICS206Hospital>) {
  const updated = plan.value.hospitals.map((h, i) => i === index ? { ...h, ...updates } : h)
  await savePlan({ hospitals: updated })
}

async function removeHospital(index: number) {
  await savePlan({ hospitals: plan.value.hospitals.filter((_, i) => i !== index) })
}

// Prepared by
const preparedBy206 = computed(() => iapStore.iap?.prepared_by_206 ?? '')
async function savePreparedBy(value: string) {
  if (!opId.value) return
  await iapStore.savePreparedBy(props.incidentId, opId.value, '206', value)
}

const levelOptions = ['ALS', 'BLS']
</script>

<template>
  <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
    <!-- Section head -->
    <div class="flex items-end gap-3.5 flex-wrap">
      <div class="flex flex-col gap-0.5">
        <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 206 · Required</span>
        <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Medical Plan</h2>
      </div>
      <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Aid stations, transport units, hospitals, and emergency procedures.</p>
    </div>

    <!-- Aid Stations group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Aid Stations</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-3">
        <div v-for="(station, idx) in plan.aid_stations" :key="idx" class="border border-border rounded p-3 flex flex-col gap-2 bg-surface-container-low group">
          <div class="grid grid-cols-3 gap-2">
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Name</label>
              <FosInput :model-value="station.name" placeholder="Aid station name…" @blur="updateAidStation(idx, { name: ($event.target as HTMLInputElement).value })" />
            </div>
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Location</label>
              <FosInput :model-value="station.location" placeholder="Location…" @blur="updateAidStation(idx, { location: ($event.target as HTMLInputElement).value })" />
            </div>
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Phone</label>
              <FosInput :model-value="station.phone" placeholder="Phone…" type="tel" @blur="updateAidStation(idx, { phone: ($event.target as HTMLInputElement).value })" />
            </div>
          </div>
          <div class="flex items-center gap-4">
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Level</label>
              <FosSelect :model-value="station.level" :options="levelOptions" @update:model-value="updateAidStation(idx, { level: $event as 'ALS' | 'BLS' })" />
            </div>
            <label :for="`para-${idx}`" class="inline-flex items-center gap-2 font-body text-[13px] text-on-surface cursor-pointer mt-4">
              <input :id="`para-${idx}`" type="checkbox" :checked="station.paramedics_on_site" class="w-3.5 h-3.5 accent-primary cursor-pointer" @change="updateAidStation(idx, { paramedics_on_site: ($event.target as HTMLInputElement).checked })" />
              Paramedics on site
            </label>
            <button class="ml-auto text-outline hover:text-error mt-4" @click="removeAidStation(idx)">
              <span class="material-symbols-outlined text-[18px]">delete</span>
            </button>
          </div>
        </div>
        <p v-if="plan.aid_stations.length === 0" class="font-body text-[13px] text-outline">No aid stations added.</p>
        <button class="inline-flex items-center gap-1.5 bg-surface border border-dashed border-border-strong rounded px-3 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary hover:border-primary self-start transition-colors" @click="addAidStation">
          <span class="material-symbols-outlined text-[14px]">add</span>
          Add Aid Station
        </button>
      </div>
    </div>

    <!-- Transport Units group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Transport Units</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-2">
        <div v-for="(t, idx) in plan.transportation" :key="idx" class="flex items-end gap-2">
          <div class="flex-1">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Service</label>
            <FosInput :model-value="t.service" placeholder="Service name…" @blur="updateTransport(idx, { service: ($event.target as HTMLInputElement).value })" />
          </div>
          <div class="flex-1">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Location</label>
            <FosInput :model-value="t.location" placeholder="Location…" @blur="updateTransport(idx, { location: ($event.target as HTMLInputElement).value })" />
          </div>
          <div class="w-32">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Phone</label>
            <FosInput :model-value="t.phone" placeholder="Phone…" type="tel" @blur="updateTransport(idx, { phone: ($event.target as HTMLInputElement).value })" />
          </div>
          <div class="w-20">
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Level</label>
            <FosSelect :model-value="t.level" :options="levelOptions" @update:model-value="updateTransport(idx, { level: $event as 'ALS' | 'BLS' })" />
          </div>
          <button class="text-outline hover:text-error mb-1.5" @click="removeTransport(idx)">
            <span class="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
        <p v-if="plan.transportation.length === 0" class="font-body text-[13px] text-outline">No transport units added.</p>
        <button class="inline-flex items-center gap-1.5 bg-surface border border-dashed border-border-strong rounded px-3 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary hover:border-primary self-start transition-colors mt-1" @click="addTransport">
          <span class="material-symbols-outlined text-[14px]">add</span>
          Add Transport Unit
        </button>
      </div>
    </div>

    <!-- Hospitals group -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Hospitals</h3>
      </div>
      <div class="p-3.5 flex flex-col gap-3">
        <div v-for="(h, idx) in plan.hospitals" :key="idx" class="border border-border rounded p-3 bg-surface-container-low">
          <div class="grid grid-cols-3 gap-2 mb-2">
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Name</label>
              <FosInput :model-value="h.name" placeholder="Hospital name…" @blur="updateHospital(idx, { name: ($event.target as HTMLInputElement).value })" />
            </div>
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Address</label>
              <FosInput :model-value="h.address" placeholder="Address…" @blur="updateHospital(idx, { address: ($event.target as HTMLInputElement).value })" />
            </div>
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Phone</label>
              <FosInput :model-value="h.phone" placeholder="Phone…" type="tel" @blur="updateHospital(idx, { phone: ($event.target as HTMLInputElement).value })" />
            </div>
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Air Travel Time</label>
              <FosInput :model-value="h.travel_time_air" placeholder="e.g. 15 min" @blur="updateHospital(idx, { travel_time_air: ($event.target as HTMLInputElement).value })" />
            </div>
            <div>
              <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Ground Travel Time</label>
              <FosInput :model-value="h.travel_time_ground" placeholder="e.g. 45 min" @blur="updateHospital(idx, { travel_time_ground: ($event.target as HTMLInputElement).value })" />
            </div>
          </div>
          <div class="flex items-center gap-4 flex-wrap">
            <label v-for="(cfg, fi) in [
              { key: 'trauma_center' as const, label: 'Trauma Center' },
              { key: 'burn_center' as const, label: 'Burn Center' },
              { key: 'helipad' as const, label: 'Helipad' },
            ]" :key="fi" class="inline-flex items-center gap-2 font-body text-[13px] text-on-surface cursor-pointer">
              <input type="checkbox" :checked="h[cfg.key]" class="w-3.5 h-3.5 accent-primary cursor-pointer" @change="updateHospital(idx, { [cfg.key]: ($event.target as HTMLInputElement).checked })" />
              {{ cfg.label }}
            </label>
            <button class="ml-auto text-outline hover:text-error" @click="removeHospital(idx)">
              <span class="material-symbols-outlined text-[18px]">delete</span>
            </button>
          </div>
        </div>
        <p v-if="plan.hospitals.length === 0" class="font-body text-[13px] text-outline">No hospitals added.</p>
        <button class="inline-flex items-center gap-1.5 bg-surface border border-dashed border-border-strong rounded px-3 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary hover:border-primary self-start transition-colors" @click="addHospital">
          <span class="material-symbols-outlined text-[14px]">add</span>
          Add Hospital
        </button>
      </div>
    </div>

    <!-- Emergency Procedures + K9 Vet Info (2-column) -->
    <div class="grid gap-3" style="grid-template-columns: 1fr 1fr;">
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Emergency Procedures</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea :model-value="plan.emergency_procedures" placeholder="Describe emergency medical procedures…" :rows="3" @blur="savePlan({ emergency_procedures: ($event.target as HTMLTextAreaElement).value })" />
        </div>
      </div>
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">K9 Veterinary Info</h3>
        </div>
        <div class="p-3.5">
          <FosTextarea :model-value="plan.k9_vet_info" placeholder="Veterinarian contact and location…" :rows="3" @blur="savePlan({ k9_vet_info: ($event.target as HTMLTextAreaElement).value })" />
        </div>
      </div>
    </div>

    <!-- Medical Unit Leader + Prepared By (2-column) -->
    <div class="grid gap-3" style="grid-template-columns: 1fr 1fr;">
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Medical Unit Leader</h3>
        </div>
        <div class="p-3.5">
          <FosInput :model-value="plan.medical_unit_leader" placeholder="Medical unit leader name…" @blur="savePlan({ medical_unit_leader: ($event.target as HTMLInputElement).value })" />
        </div>
      </div>
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
        </div>
        <div class="p-3.5">
          <FosInput :model-value="preparedBy206" placeholder="Name of person preparing ICS 206…" @blur="savePreparedBy(($event.target as HTMLInputElement).value)" />
        </div>
      </div>
    </div>

    <!-- Section footer -->
    <div class="flex items-center gap-2.5 mt-2 pt-3 border-t border-border/60">
      <button class="inline-flex items-center gap-1.5 bg-surface border border-border rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-on-surface cursor-pointer hover:bg-surface-container-low transition-colors" @click="$emit('prev')">
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        ICS 205
      </button>
      <span class="flex-1" />
      <button class="inline-flex items-center gap-1.5 bg-primary border border-primary rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-white cursor-pointer hover:opacity-90 transition-opacity" @click="$emit('next')">
        ICS 207
        <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
      </button>
    </div>
  </div>
</template>
